import { List, ListItem, ListItemText, Popover, Skeleton } from '@mui/material';
import { useFetchHubsQuery } from '../Api/Trinium/Endpoints/Hub/slice';
import ApplicationError from '../Infrastructure/Components/ErrorHandling/ApplicationError';
import { ApplicationErrorType } from '../Infrastructure/Components/ErrorHandling/types';
import HubListItem from './HubListItem';
import styles from './HubList.module.css';
import useCurrentTerminal from '@hooks/useCurrentTerminal';

/** Placeholder rows shown while the hubs load, so the popover opens at a stable size instead of on a spinner */
const skeletonRows = [0, 1];

/**
 * MUI Popover with a list of Hub locations
 */
export default function HubList(props: { anchor: HTMLDivElement | null; legIds: number[]; onClose: () => void }) {
	const { number: terminalNumber } = useCurrentTerminal();

	const { currentData, isError, refetch, isLoading } = useFetchHubsQuery(terminalNumber);

	if (isError) {
		return (
			<ApplicationError
				type={ApplicationErrorType.NotFound}
				messageParam={terminalNumber}
				customAction={{
					buttonText: 'Retry',
					action: () => {
						// If the refetch errors, isError will be true again
						void refetch();
					}
				}}
			/>
		);
	}

	return (
		<Popover
			open={Boolean(props.anchor)}
			anchorEl={props.anchor}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right'
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'left'
			}}
		>
			<List
				className={styles.actions}
				disablePadding
				dense
			>
				<ListItem className={styles['hub-header']}>
					<ListItemText className={styles['hub-name-item']}>Name</ListItemText>
					<ListItemText className={styles['hub-code-item']}>Code</ListItemText>
				</ListItem>
				{isLoading ? (
					skeletonRows.map((row) => (
						<ListItem key={`hub-skeleton-${String(row)}`}>
							<div className={styles['hub-name-item']}>
								<Skeleton variant='text' />
							</div>
							<div className={styles['hub-code-item']}>
								<Skeleton variant='text' />
							</div>
						</ListItem>
					))
				) : currentData?.length === 0 ? (
					<ListItem className={styles.empty}>
						<ListItemText>No Available Hubs</ListItemText>
					</ListItem>
				) : (
					currentData?.map((query) => (
						<HubListItem
							key={query.id}
							legIds={props.legIds}
							code={query.id}
							name={query.name}
							terminalNumber={terminalNumber}
							onClose={props.onClose}
						/>
					))
				)}
			</List>
		</Popover>
	);
}
